import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import "./Input.css";
import { useSelector } from "react-redux";
import { LoadingState } from "../../../redux/reducers/getLoadingReducer/getLoadingReducer";
import { defaultMatcher, getFormErrorByName, MatchingStrategy, useFormErrorContext } from "../../../utils/context/FormErrorContext";
import { selectGetLoading, selectMenu } from "../../../redux/selectors/exportSelectorPaths";

export interface InputProps {
    name: string,
    id: string,
    type: "text" | "password" | "number" | "hidden" | "checkbox"
    className?: string,
    title?: string,
    placeholder?: string,
    ariaLabel?: string,
    label?: string,
    disabled?: boolean,
    onInput?: (e: string | null) => void;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>, params?: unknown[]) => void;
    onBlur?: React.ChangeEventHandler;
    onPaste?: React.ClipboardEventHandler;
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
    noTimer?: boolean;
    loading?: boolean;
    value?: string | null;
    isError?: boolean;
    isValid?: boolean;
    autoComplete?: string;
    errorMessage?: string;
    hideErrorCross?: boolean;
    maxLength?: number;
    size?: number;
    testid?: string;
    readOnly?: boolean;
    autoCompleteAPIEnabled?: boolean;
    autoCompleteAPIItems?: Array<string>;
    table?: boolean;
    forcedType?: string | undefined;
    positiveOnly?: boolean;
    primaryKey?: string;
    errorMatcher?: MatchingStrategy;
    useIdForError?: boolean;
}

export default function Input(props: InputProps): JSX.Element {

    const [val, setVal] = useState(props.value);
    const [showPassword, setShowPassword] = useState(false);
    const { isReadOnly } = useReadOnly()
    const loadingState: LoadingState = useSelector(selectGetLoading);
    const menuState = useSelector(selectMenu);
    const { errors } = useFormErrorContext();
    const matcher = props.errorMatcher ? props.errorMatcher : defaultMatcher



    useEffect(() => {
        setVal(props.value);
    }, [props.value]);

    useEffect(() => {

        const handleInput = (e: string | null | undefined) => {
            if (typeof props.onInput === "function" && e !== undefined) {
                props.onInput(e);
            }
            return;
        }

        if (props.noTimer) {
            handleInput(val);
            return;
        } else {
            const timeOutId = setTimeout(() => handleInput(val), 1000);
            return () => clearTimeout(timeOutId)
        }

    }, [val, props]);

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();
        const pastedText = event.clipboardData.getData('text');
        if (props.type === "number") {
            // Remove unwanted characters
            const filteredText = pastedText.replace(/[eE+-]/g, '');
            // Insert the filtered text back into the input
            document.execCommand('insertText', false, filteredText);
        }
        else {
            document.execCommand('insertText', false, pastedText);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (props.type === "number") {
            // If the pressed key is "V" and the Ctrl or Cmd key is held
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                // Remove unwanted characters
                e.currentTarget.value.replace(/[eE+-]/g, '');
                return;
            }

            // Prevent unwanted characters like 'e', 'E', '+', '-'
            if (["e", "E", "+", "-"].includes(e.key)) {
                e.preventDefault();
            }
        }
    }
    // const autoCompleteItem = (value: AutoCompleteItem) => {
    //     return (
    //         <div onClick={() => {
    //             setVal(value.address);
    //             dispatch({ type: AUTOCOMPLETE.RESET });
    //         }}><strong>{`${value.address} - ${value.city}`}</strong></div>
    //     )
    // };

    const updateValueByModifiers = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target

        if (props.positiveOnly) {
            if (value.includes("-")) {
                return value.replace("-", "")
            }
        }

        if (!props.forcedType) {
            return value;
        }

        if (value === '') {
            return value;
        }

        if (props.forcedType === 'integer') {
            const intValue = parseInt(value, 10)
            if (isNaN(intValue)) {
                return ''
            }
            return String(intValue)
        } else if (props.forcedType === 'float') {
            return String(/^\d*\.?\d*$/.test(value) ? value : '')
        }

        return value;
    }

    const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.type === "number" && props.forcedType !== "float") {
            const target = e.target
            const value = e.target.value
            const trimValue = (value && value.trim()) ? value.trim() : null
            let stringValue = ""
            if (trimValue) {
                const numberValue = Number(trimValue)
                if (!isNaN(numberValue)) {
                    stringValue = String(numberValue)
                }
            }
            target.value = stringValue
        }
        if (props.onChange) {
            props.onChange(e);
            setVal(updateValueByModifiers(e))
        } else {
            setVal(updateValueByModifiers(e))
        }
    }

    const focusOut = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.onBlur) {
            props.onBlur(e);
            setVal((e.target as HTMLInputElement).value);
        }
    }

    const [inputClassName, setInputClassName] = useState(`${props.table ? "table-input" : "table"}${props.type === "password" ? " password-input" : ""}${props.className ? " " + props.className : ""}${props.isError ? " error" : ""}`)
    useEffect(() => {
        const inputClassName = `${props.table ? "table-input" : "table"}${props.type === "password" ? " password-input" : ""}${props.className ? (" " + props.className) : ""}${props.isError ? " error" : ""}`
        setInputClassName(inputClassName)
    }, [loadingState.loading, menuState.activeItem, props.className, props.isError, props.name, props.table, props.type, val])

    return (
        <>
            <input
                title={props.title ?? props.label}
                readOnly={props.readOnly}
                value={val ? val : ""}
                className={inputClassName}
                onChange={(e) => { changeHandler(e) }}
                type={props.type === "password" ? (showPassword ? "text" : "password") : props.type}
                id={props.id}
                name={props.name} placeholder={props.placeholder}
                disabled={props.disabled || isReadOnly}
                autoComplete="off"
                aria-label={props.ariaLabel}
                data-testid={props.testid ?? "input-test"}
                onPaste={props.onPaste ? props.onPaste : handlePaste}
                maxLength={props.maxLength ? props.maxLength : 250}
                size={props.size}
                onBlur={(e) => focusOut(e)}
                onWheel={(e) => {
                    if (props.type === "number") {
                        e.currentTarget.blur()
                    }
                }}
                onKeyDown={props.onKeyDown ? props.onKeyDown : handleKeyDown}
                data-primary-key={getFormErrorByName(errors, props.useIdForError ? props.id : props.name, matcher)?.primaryKey as string}
            >

            </input>

            {props.type === "password" && <button type="button" className="show-password-button" onClick={() => setShowPassword(!showPassword)} aria-label="show-password-button"><FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} /></button>}
            {/* <ValidateResultIcon isValid={props.isValid} /> */}
            {/* {props.loading ? <AmpInlineLoadingSpinner /> : ""} */}
            {props.errorMessage ? <div className="input-error-message">{!props.hideErrorCross && <FontAwesomeIcon className="red-cross" icon={faCircleXmark} />}{props.errorMessage}</div> : ""}
        </>
    )
}